import { Check, Copy } from "lucide-react";
import { Fragment, useState } from "react";

import { Button } from "@/components/ui/button";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { useDocsContent } from "./content";
import { urlSegments } from "./utils";

/** How long "Copied" shows after a copy. */
const COPIED_MS = 2_000;

/** The connector's URL to add in Claude, with a copy button. */
export function ConnectorUrl({ url }: { url: string }) {
  const { copyUrl: text } = useDocsContent();
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      logger.debug("[docs]", "copied the connector URL");
      setState("copied");
      window.setTimeout(() => setState("idle"), COPIED_MS);
    } catch (err) {
      logger.warn("[docs] could not copy the connector URL", err);
      setState("failed");
    }
  };

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2">
        <code className="bg-accent-light text-foreground block min-w-0 flex-1 rounded-md px-3 py-2 font-mono text-xs [overflow-wrap:anywhere]">
          {/* A narrow column breaks the URL after a path slash, not mid-word. */}
          {urlSegments(url).map((segment, index) => (
            <Fragment key={index}>
              {segment}
              <wbr />
            </Fragment>
          ))}
        </code>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => void copy()}
          aria-label={state === "copied" ? text.copied : text.label}
        >
          {state === "copied" ? (
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {state === "copied" ? text.copied : text.button}
        </Button>
      </div>
      <p
        role="status"
        className={cn("text-xs", state === "failed" ? "text-muted mt-1.5" : "sr-only")}
      >
        {state === "copied" && text.copiedStatus}
        {state === "failed" && text.failed}
      </p>
    </div>
  );
}
